import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { PRODUCTS, COMPANY } from "@/data/products";
import { ArrowRight, Check, MessageCircle, Download } from "lucide-react";

export default function Products() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        // Slight delay for layout
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [hash]);

  return (
    <div data-testid="page-products">
      {/* Hero / Intro */}
      <section className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16 lg:pt-28 lg:pb-20">
          <p className="label-eyebrow">/ Mineral Portfolio</p>
          <h1 className="font-heading mt-4 text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter max-w-4xl">
            Seven commodities. <br />
            <span className="copper-text">One product line.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-400 leading-relaxed">
            EELLEVEN MINING supplies copper cathodes, copper wire scrap,
            copper concentrate, copper ore, cobalt ore,
            manganese ore and coal — sourced from Zambia
            and the wider Central African region.
          </p>
        </div>
      </section>

      {/* Quick Index Grid */}
      <section className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800">
            {PRODUCTS.map((p, i) => (
              <a
                key={p.slug}
                href={`#${p.slug}`}
                data-testid={`product-index-${p.slug}`}
                className="group bg-[var(--eel-surface)] p-5 hover:bg-[#1f1f22] transition-colors"
              >
                <p className="label-eyebrow text-zinc-500">
                  {String(i + 1).padStart(2, "0")} · {p.category}
                </p>
                <p className="mt-2 font-heading font-bold text-sm md:text-base group-hover:text-[var(--eel-copper)] transition-colors">
                  {p.name}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Sections */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 space-y-32">
        {PRODUCTS.map((p, idx) => (
          <ProductSection key={p.slug} product={p} index={idx} />
        ))}
      </div>

      {/* Bottom CTA */}
      <section className="border-t border-zinc-800 bg-[#0c0c0e]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="label-eyebrow">/ Procurement</p>
            <h3 className="font-heading mt-3 text-2xl md:text-4xl font-black uppercase tracking-tighter">
              Ready to request a quote?
            </h3>
            <p className="mt-3 text-zinc-400 max-w-xl">
              Share product, tonnage, destination and incoterms. We respond
              within 24 hours.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="products-bottom-cta"
              className="px-6 py-3 copper-bg text-black text-sm font-semibold uppercase tracking-widest hover:bg-[var(--eel-copper-hover)] transition-colors inline-flex items-center gap-3"
            >
              Request Quote <ArrowRight size={14} />
            </Link>
            <a
              href={`https://wa.me/${COMPANY.whatsappPrimaryDigits}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="products-bottom-wa"
              className="px-6 py-3 border border-zinc-700 text-sm font-semibold uppercase tracking-widest hover:border-white inline-flex items-center gap-3"
            >
              <MessageCircle size={14} className="text-green-500" /> WhatsApp Desk
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function ProductSection({ product, index }) {
  const reverse = index % 2 === 1;
  return (
    <section
      id={product.slug}
      data-testid={`product-section-${product.slug}`}
      className="scroll-mt-24"
    >
      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-start ${reverse ? "lg:[direction:rtl]" : ""}`}>
        <div className={`lg:col-span-5 lg:[direction:ltr]`}>
          <div className="border border-zinc-800 overflow-hidden">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-[440px] object-cover"
              loading="lazy"
            />
          </div>
        </div>
        <div className="lg:col-span-7 lg:[direction:ltr]">
          <p className="label-eyebrow">
            / {String(index + 1).padStart(2, "0")} · {product.category}
          </p>
          <h2 className="font-heading mt-3 text-3xl md:text-5xl font-black uppercase tracking-tighter">
            {product.name}
          </h2>
          <p className="mt-2 copper-text font-heading uppercase tracking-widest text-xs">
            {product.tagline}
          </p>

          <div className="mt-6 space-y-4 text-zinc-300 leading-relaxed">
            {product.body.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <ul className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            {product.pillars.map((pillar, i) => (
              <li
                key={i}
                className="border border-zinc-800 p-3 text-xs text-zinc-300 flex items-start gap-2"
              >
                <Check size={14} className="copper-text mt-0.5 shrink-0" />
                {pillar}
              </li>
            ))}
          </ul>

          <div className="mt-8 border border-zinc-800">
            <div className="flex items-center justify-between px-5 py-3 bg-zinc-900 border-b border-zinc-800">
              <span className="label-eyebrow text-zinc-300">
                Specification
              </span>
              <button
                onClick={() => window.print()}
                data-testid={`product-print-${product.slug}`}
                className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-2"
              >
                <Download size={12} /> Print
              </button>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {product.specs.map(([k, v], i) => (
                  <tr
                    key={i}
                    className={`${
                      i % 2 === 0
                        ? "bg-[var(--eel-surface)]"
                        : "bg-transparent"
                    } border-b border-zinc-800 last:border-b-0`}
                  >
                    <td className="px-5 py-2.5 text-zinc-500 uppercase tracking-widest text-xs w-1/3">
                      {k}
                    </td>
                    <td className="px-5 py-2.5 text-zinc-200">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/contact`}
              data-testid={`product-cta-quote-${product.slug}`}
              className="px-5 py-3 copper-bg text-black text-xs font-semibold uppercase tracking-widest hover:bg-[var(--eel-copper-hover)] transition-colors inline-flex items-center gap-2"
            >
              Request Quote <ArrowRight size={12} />
            </Link>
            <a
              href={`https://wa.me/${COMPANY.whatsappPrimaryDigits}?text=${encodeURIComponent(
                `Hello EELLEVEN MINING, I'm interested in ${product.name}.`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`product-cta-wa-${product.slug}`}
              className="px-5 py-3 border border-zinc-700 text-xs font-semibold uppercase tracking-widest hover:border-white inline-flex items-center gap-2"
            >
              <MessageCircle size={12} className="text-green-500" /> WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
